import { apiClient } from './client'
import { API_ENDPOINTS } from './endpoints'

export type ReportLayoutType =
  | 'table'
  | 'list'
  | 'bar'
  | 'line'
  | 'area'
  | 'pie'
  | 'donut'
  | 'treemap'

export const REPORT_LAYOUTS: { value: ReportLayoutType; labelAr: string; labelEn: string }[] = [
  { value: 'table', labelAr: 'جدول', labelEn: 'Table' },
  { value: 'list', labelAr: 'قائمة', labelEn: 'List' },
  { value: 'bar', labelAr: 'أعمدة', labelEn: 'Bar' },
  { value: 'line', labelAr: 'خطي', labelEn: 'Line' },
  { value: 'area', labelAr: 'مساحي', labelEn: 'Area' },
  { value: 'pie', labelAr: 'دائري', labelEn: 'Pie' },
  { value: 'donut', labelAr: 'حلقي', labelEn: 'Donut' },
  { value: 'treemap', labelAr: 'شجري', labelEn: 'Tree' },
]

export function isChartLayout(layout?: string | null): boolean {
  const t = (layout || 'table').toLowerCase()
  return t !== 'table' && t !== 'list'
}

export const CHART_RUN_PAGE_SIZE = 500

export type DisplayType =
  | 'Text'
  | 'Integer'
  | 'Decimal'
  | 'Currency'
  | 'Date'
  | 'DateTime'
  | 'Boolean'
  | 'Dropdown'
  | 'SearchableDropdown'

export interface DynamicReportField {
  id?: number
  reportId?: number
  fieldName: string
  alias?: string
  dataType?: string
  displayType: DisplayType | string
  displayOrder: number
  isVisible: boolean
  isFilterable: boolean
  isSortable: boolean
  isGroupable: boolean
  format?: string
  lookupView?: string
  lookupValueField?: string
  lookupDisplayField?: string
  aggregation?: string | null
  sortDirection?: string | null
  sortOrder?: number | null
}

export interface DynamicReportFilter {
  id?: number
  reportId?: number
  fieldId?: number
  fieldName?: string
  operator: string
  defaultValue?: string
  isRequired: boolean
  displayOrder: number
}

export interface DynamicReport {
  id: number
  name: string
  menuName?: string
  description?: string
  schemaName: string
  viewName: string
  category?: string
  icon?: string
  layoutType?: ReportLayoutType | string
  categoryField?: string
  valueField?: string
  defaultPageSize?: number
  isActive: boolean
  fields: DynamicReportField[]
  filters: DynamicReportFilter[]
}

export function suggestChartFields(fields: DynamicReportField[]): { categoryField: string; valueField: string } {
  const visible = fields.filter((f) => f.isVisible)
  const numeric = visible.filter((f) => ['Integer', 'Decimal', 'Currency'].includes(String(f.displayType)))
  const category = visible.find((f) => !numeric.some((n) => n.fieldName === f.fieldName)) || visible[0]
  return {
    categoryField: category?.fieldName || '',
    valueField: numeric[0]?.fieldName || '',
  }
}

export interface SqlViewInfo {
  schemaName: string
  viewName: string
  description?: string
  createDate?: string
  modifyDate?: string
  fieldCount: number
  reportCount: number
  definition?: string
  selectSql?: string
  columns?: SqlViewColumn[]
}

export interface SqlSchemaObject {
  schemaName: string
  name: string
  objectType: 'table' | 'view' | string
}

export interface SqlViewColumn {
  columnName: string
  dataType: string
  isNullable: boolean
  ordinalPosition: number
  maxLength?: number
  precision?: number
  scale?: number
  suggestedDisplayType?: DisplayType | string
}

export interface DynamicReportRunResult {
  reportId: number
  name: string
  columns: DynamicReportField[]
  rows: Record<string, unknown>[]
  totals?: Record<string, unknown> | null
  totalCount: number
  page: number
  pageSize: number
}

export function suggestAlias(columnName: string): string {
  if (!columnName) return ''
  if (/[\u0600-\u06FF]/.test(columnName)) return columnName
  return columnName
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
}

export function suggestDisplayType(dataType?: string): DisplayType {
  const t = (dataType || '').toLowerCase()
  if (['int', 'bigint', 'smallint', 'tinyint'].includes(t)) return 'Integer'
  if (['decimal', 'numeric', 'money', 'smallmoney', 'float', 'real'].includes(t)) return 'Decimal'
  if (t === 'date') return 'Date'
  if (['datetime', 'datetime2', 'smalldatetime', 'datetimeoffset'].includes(t)) return 'DateTime'
  if (t === 'bit') return 'Boolean'
  return 'Text'
}

export function defaultOperator(displayType?: string): string {
  const t = (displayType || '').toLowerCase()
  if (t === 'date' || t === 'datetime' || t === 'integer' || t === 'decimal' || t === 'currency') return 'between'
  if (t === 'dropdown' || t === 'searchabledropdown') return '='
  return 'contains'
}

export const dynamicReportService = {
  list: async () => (await apiClient.get<DynamicReport[]>(API_ENDPOINTS.dynamicReports.list)).data,
  menu: async () => (await apiClient.get(API_ENDPOINTS.dynamicReports.menu)).data as Array<{
    id: number
    name: string
    menuName: string
    description?: string
    icon?: string
    path: string
  }>,
  get: async (id: number | string) => (await apiClient.get<DynamicReport>(API_ENDPOINTS.dynamicReports.get(id))).data,
  create: async (report: Partial<DynamicReport>) =>
    (await apiClient.post<DynamicReport>(API_ENDPOINTS.dynamicReports.create, report)).data,
  update: async (id: number | string, report: Partial<DynamicReport>) =>
    (await apiClient.put<DynamicReport>(API_ENDPOINTS.dynamicReports.update(id), report)).data,
  delete: async (id: number | string) => apiClient.delete(API_ENDPOINTS.dynamicReports.delete(id)),
  run: async (payload: {
    reportId: number
    page?: number
    pageSize?: number
    sort?: { field: string; direction: string }[]
    filters?: Record<string, unknown>
  }) => (await apiClient.post<DynamicReportRunResult>(API_ENDPOINTS.dynamicReports.run, payload)).data,
  preview: async (report: Partial<DynamicReport>) =>
    (await apiClient.post<DynamicReportRunResult>(API_ENDPOINTS.dynamicReports.preview, report)).data,
  export: async (id: number | string, format: string, payload: Record<string, unknown>) =>
    apiClient.post(API_ENDPOINTS.dynamicReports.export(id, format), payload, { responseType: 'blob' }),
  lookup: async (id: number | string, fieldName: string) =>
    (await apiClient.get(API_ENDPOINTS.dynamicReports.lookup(id, fieldName))).data as Array<{ value: unknown; label: unknown }>,
  schemaObjects: async () =>
    (await apiClient.get<SqlSchemaObject[]>(API_ENDPOINTS.dynamicReports.schemaObjects)).data,
  objectColumns: async (schema: string, name: string) =>
    (await apiClient.get<SqlViewColumn[]>(API_ENDPOINTS.dynamicReports.objectColumns(schema, name))).data,
  views: async () => (await apiClient.get<SqlViewInfo[]>(API_ENDPOINTS.dynamicReports.views)).data,
  view: async (schema: string, name: string) =>
    (await apiClient.get<SqlViewInfo>(API_ENDPOINTS.dynamicReports.view(schema, name))).data,
  viewColumns: async (schema: string, name: string) =>
    (await apiClient.get<SqlViewColumn[]>(API_ENDPOINTS.dynamicReports.viewColumns(schema, name))).data,
  viewPreview: async (schema: string, name: string) =>
    (await apiClient.post<Record<string, unknown>[]>(API_ENDPOINTS.dynamicReports.viewPreview(schema, name))).data,
  previewSql: async (sql: string) =>
    (await apiClient.post<Record<string, unknown>[]>(API_ENDPOINTS.dynamicReports.previewSql, { sql })).data,
  createView: async (viewName: string, sql: string) =>
    (await apiClient.post(API_ENDPOINTS.dynamicReports.createView, { viewName, sql })).data as {
      schemaName: string
      viewName: string
    },
  updateView: async (schema: string, name: string, sql: string) =>
    (await apiClient.put(API_ENDPOINTS.dynamicReports.updateView(schema, name), { sql })).data as {
      schemaName: string
      viewName: string
    },
  deleteView: async (schema: string, name: string) =>
    apiClient.delete(API_ENDPOINTS.dynamicReports.deleteView(schema, name)),
}
